import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Modal } from './Modal';
import { FormattedMessage } from '../_library/intl';
import { useAppSelector, api } from '../_library';
import { i18nActions } from '../_actions';
import { UI_LANGUAGE_CODES } from '../_constants';
import localeDataEn from '../translations/en.json';
import localeDataLv from '../translations/lv.json';
import localeDataRu from '../translations/ru.json';

type LabelMap = { [key: string]: string };

// All languages bundled with the app. Only UI_LANGUAGE_CODES (en, lv) are shown in
// the editor; ru stays bundled so its file default can seed the platform (see save()).
const bundled: { [lang: string]: LabelMap } = {
    en: localeDataEn,
    lv: localeDataLv,
    ru: localeDataRu
};

// Languages that ship in the bundle but are intentionally hidden from the UI (ru).
const HIDDEN_LANGS = Object.keys(bundled).filter(lang => !UI_LANGUAGE_CODES.includes(lang));

type Props = {};

export const LabelEditModal: (props: Props) => JSX.Element | null = () => {

    const dispatch = useDispatch();
    const editingKey = useAppSelector(state => state.i18n.editingKey);
    const overrides = useAppSelector(state => state.i18n.overrides) as { [lang: string]: LabelMap };

    const [values, setValues] = useState<{ [lang: string]: string }>({});
    const [initialValues, setInitialValues] = useState<{ [lang: string]: string }>({});
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (editingKey) {
            const initial: { [lang: string]: string } = {};
            UI_LANGUAGE_CODES.forEach(lang => {
                initial[lang] = (overrides[lang] && overrides[lang][editingKey]) ?? bundled[lang][editingKey] ?? '';
            });
            setValues(initial);
            setInitialValues(initial);
            setError(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingKey]);

    const close = () => {
        dispatch(i18nActions.setEditingLabelKey(null));
    };

    const save = () => {
        if (!editingKey) {
            return;
        }

        // Only send languages the editor actually changed to a non-empty value.
        // Sending an untouched or locally-absent language (seeded as '') would
        // overwrite a real translation on the platform with an empty string.
        const payload: { [lang: string]: string } = {};
        UI_LANGUAGE_CODES.forEach(lang => {
            if ((values[lang] ?? '') !== '' && values[lang] !== initialValues[lang]) {
                payload[lang] = values[lang];
            }
        });

        if (Object.keys(payload).length === 0) {
            close();
            return;
        }

        // A real update is happening, so seed the platform's ru (a bundled-but-hidden
        // language) from the file default on its FIRST edit: only when the platform has
        // no known ru value do we add the bundled default to this same PATCH. A known
        // platform ru value is left out entirely, so ru is seeded once and never overwritten.
        HIDDEN_LANGS.forEach(lang => {
            const known = (overrides && overrides[lang]) ? overrides[lang][editingKey] : undefined;
            const fileDefault = bundled[lang][editingKey];
            if ((known === undefined || known === '') && fileDefault !== undefined && fileDefault !== '') {
                payload[lang] = fileDefault;
            }
        });

        setSaving(true);
        setError(null);
        api.patch('/labels/live', { key: editingKey, values: payload })
            .then(res => {
                setSaving(false);
                dispatch(i18nActions.labelSaved(res.data.key, res.data.values));
                close();
            })
            .catch(err => {
                setSaving(false);
                if (err.response && err.response.status === 400 && err.response.data && err.response.data.error === 'character_limit_exceeded') {
                    setError('bk.label_edit.error_char_limit');
                } else {
                    setError('bk.label_edit.error_generic');
                }
            });
    };

    if (!editingKey) {
        return null;
    }

    const content = (
        <div>
            <div className="font-mono text-sm bg-gray-100 text-gray-700 p-2 rounded mb-4 break-all">
                {editingKey}
            </div>
            {UI_LANGUAGE_CODES.map(lang => (
                <div key={lang} className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1 uppercase">{lang}</label>
                    <textarea
                        className="w-full border border-gray-300 rounded p-2 text-sm"
                        rows={2}
                        value={values[lang] ?? ''}
                        onChange={e => {
                            const next = e.target.value;
                            setValues(prev => ({ ...prev, [lang]: next }));
                        }}
                    />
                </div>
            ))}
            {error &&
                <div className="text-bk-red text-sm mb-3">
                    <FormattedMessage id={error} />
                </div>
            }
            <div className="flex justify-end gap-2 mt-4">
                <button
                    type="button"
                    className="px-4 py-2 border border-gray-300 rounded text-gray-700"
                    onClick={close}
                    disabled={saving}
                >
                    <FormattedMessage id="bk.label_edit.cancel" />
                </button>
                <button
                    type="button"
                    className="px-4 py-2 bg-bk-pink-200 text-white rounded disabled:opacity-50"
                    onClick={save}
                    disabled={saving}
                >
                    <FormattedMessage id="bk.label_edit.save" />
                </button>
            </div>
        </div>
    );

    return (
        <Modal
            isOpen={!!editingKey}
            toggle={close}
            title="bk.label_edit.title"
            content={content}
        />
    );
};
